import React, { createContext, useContext, useMemo, useState } from "react";
import FeedbackSnackbar from "~/presentation/components/FeedbackSnackbar";
import { apiEnglishWords } from "./englishWords";

export const SnackbarContext = createContext();
export const useSnackbar = () => useContext(SnackbarContext);

export const SnackbarProvider = ({ children }) => {
  const [snackbar, setSnackbar] = useState({
    isOpen: false, //sempre enviamos como {isOpen: true} para chamar.
    message: "Message!", //mensagem de texto
    type: "warning", //info, warning, error, success
  });

  const openSnackbar = (props) => setSnackbar({ isOpen: true, ...props });
  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, isOpen: false }));

  //Ver se o erro está em Português-Brasil
  const isErrorMessageInPTBR = (message) => {
    if (!message || typeof message !== "string") return false;

    return !apiEnglishWords.some((word) => message.includes(word));
  };

  const extractMessageFromError = (err) => {
    if (!err) return null;
    if (typeof err === "string") return err;

    const data = err.response?.data || err.data;
    if (data) {
      if (typeof data === "string" && data.trim()) return data;
      if (typeof data === "object") {
        if (typeof data.message === "string") return data.message;
        if (typeof data.detail === "string") return data.detail;
        if (typeof data.error === "string") return data.error;
        if (typeof data.title === "string" && isErrorMessageInPTBR(data.title)) return data.title;

        if (data.errors) {
          if (typeof data.errors === "string") return data.errors;
          if (Array.isArray(data.errors) && data.errors.length > 0) {
            const first = data.errors[0];
            if (typeof first === "string") return first;
            if (first && typeof first.message === "string") return first.message;
          }
          if (typeof data.errors === "object") {
            const firstKey = Object.keys(data.errors)[0];
            const val = data.errors[firstKey];
            if (Array.isArray(val) && val.length > 0 && typeof val[0] === "string") return val[0];
            if (typeof val === "string") return val;
          }
        }
      }
    }

    if (typeof err.message === "string" && err.message !== "[object Object]") {
      return err.message;
    }
    if (typeof err.error === "string") return err.error;
    if (typeof err.detail === "string") return err.detail;

    return null;
  };

  const showError = (apiErrorMsg, frontErrorMsg = "Erro:") => {
    let errorMsg = "";
    const extractedMsg = extractMessageFromError(apiErrorMsg);

    if (isErrorMessageInPTBR(extractedMsg)) {
      if (!frontErrorMsg || frontErrorMsg === "Erro:" || frontErrorMsg === "Erro") {
        errorMsg = extractedMsg;
      } else {
        const prefix = frontErrorMsg.endsWith(":") ? frontErrorMsg.slice(0, -1) : frontErrorMsg;
        errorMsg = `${prefix}: ${extractedMsg}`;
      }
    } else {
      errorMsg = frontErrorMsg || "Ocorreu um erro no servidor.";
    }

    setSnackbar({
      isOpen: true,
      message: errorMsg,
      type: "error",
    });
  };

  const showSuccess = (message = "") => {
    setSnackbar({
      isOpen: true,
      message: message,
      type: "success",
    });
  };

  const showInfo = (message = "") => {
    setSnackbar({
      isOpen: true,
      message: message,
      type: "info",
    });
  };

  const showWarning = (warningMessage = null, message = "") => {
    setSnackbar({
      isOpen: true,
      message: message + warningMessage,
      type: "warning",
    });
  };

  const showSnackbar = (message, type = "info") => {
    setSnackbar({
      isOpen: true,
      message: message,
      type: type,
    });
  };

  const value = {
    setFeedbackSnackbar: openSnackbar,
    closeSnackbar,
    showError,
    showInfo,
    showSuccess,
    showWarning,
    showSnackbar,
  };

  return (
    <SnackbarContext.Provider value={useMemo(() => value, [])}>
      {children}
      <FeedbackSnackbar
        isOpen={snackbar.isOpen}
        message={snackbar.message}
        type={snackbar.type}
        onClose={closeSnackbar}
      />
    </SnackbarContext.Provider>
  );
};
